import {track} from 'iq-tracking';
import type {SubmissionFlowData} from 'iq-interview_experiences/types';

export const trackSubmissionFlowStepViewed = (step: number, submissionId: string): Promise<void> =>
  track({
    event: 'submission_flow_step_viewed',
    action: 'view',
    component: 'page',
    content_type: 'experience',
    extra: {extra_data: JSON.stringify({submission_flow_id: submissionId, step})},
  });

export const trackSubmissionFlowSubmitted = (
  submissionId: string,
  companySlug?: string,
): Promise<void> =>
  track({
    event: 'submission_flow_submitted',
    action: 'submit',
    component: 'page',
    content_type: 'experience',
    content_identifier: companySlug,
    extra: {extra_data: JSON.stringify({submission_flow_id: submissionId})},
  });

export const trackSubmissionFlowStepCompleted = (
  step: number,
  data: SubmissionFlowData,
  submissionId: string,
  validation?: {valid: boolean; reason: string} | null,
): Promise<void> => {
  const stepFields = (() => {
    switch (step) {
      case 0:
        return {
          company: data.company,
          position: data.position,
          outcome: data.outcome,
          date: data.customDate,
          location: data.location,
        };
      case 1:
        return {experience: data.experience};
      default:
        return {questions: data.questions, takehome_files: data.takeHomeFiles.length};
    }
  })();

  return track({
    event: 'step_submitted',
    action: 'submit',
    component: 'page',
    content_type: 'experience',
    content_identifier: data.company?.value ?? undefined,
    extra: {
      extra_data: JSON.stringify({
        submission_flow_id: submissionId,
        step,
        step_validation: validation ?? null,
        ...stepFields,
      }),
    },
  });
};

export const trackDiaformOpened = (submissionId: string): Promise<void> =>
  track({
    event: 'submission_flow_diaform_opened',
    action: 'click',
    component: 'page',
    content_type: 'experience',
    extra: {extra_data: JSON.stringify({submission_flow_id: submissionId})},
  });

// Called from submit_experience_banner, experiences_paywall, and unlock_modal.
export const trackSubmitExperienceOpened = (
  trigger: 'banner' | 'barrier' | 'modal',
): Promise<void> =>
  track({
    event: 'submit_experience_opened',
    action: 'open',
    component: trigger,
    content_type: 'experience',
  });

// Called from experiences_paywall, unlock_modal, and subscribe_premium_banner.
export const trackSubscribeClicked = (trigger: 'banner' | 'barrier' | 'modal'): Promise<void> =>
  track({
    event: 'subscribe_clicked',
    action: 'click',
    component: trigger,
    content_type: 'subscription',
  });
